import React, { useState } from "react";
import "./Register.css";
import { useNavigate } from "react-router-dom";
import { setUserCookie } from "../../context/cookieUtils";
import { useTranslation } from "react-i18next";
import Button from "../../OwlyManage/components/Buttons/Button/Button";
import ConfirmModal from "../../OwlyManage/components/ConfirmModal/ConfirmModal";

export default function Register({ onBack }) {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [agree, setAgree] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!username || !password || !email || !passwordConfirm) {
      setError(t('register.errors.complete_all'));
      return;
    }
    if (!agree) {
      setError(t('login.errors.must_accept_terms'));
      return;
    }
    try {
      const signupUrl =
        process.env.NODE_ENV === "production"
          ? process.env.REACT_APP_BASE_URL_PROD + "/api/auth/signup/"
          : process.env.REACT_APP_BASE_URL_DEV + "/api/auth/signup/";
      const response = await fetch(signupUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username,
          name: name.trim() !== "" ? name : undefined,
          email,
          password,
          password_confirm: passwordConfirm,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        // Si la respuesta es un objeto con arrays de mensajes por campo
        if (typeof data === "object" && data !== null && !Array.isArray(data)) {
          // Diccionario para mapear los nombres de campo a su label
          const fieldLabels = {
            username: t('register.fieldLabels.username'),
            name: t('register.fieldLabels.name'),
            email: t('register.fieldLabels.email'),
            password: t('register.fieldLabels.password'),
            password_confirm: t('register.fieldLabels.password_confirm'),
          };
          // Junta todos los mensajes formateados: 'Label: mensaje'
          const mensajes = Object.entries(data).flatMap(([field, msgs]) => {
            const label = fieldLabels[field] || field;
            return Array.isArray(msgs)
              ? msgs.map((msg) => `${label}: ${msg}`)
              : [`${label}: ${msgs}`];
          });
          setError(mensajes);
        } else if (data.message) {
          setError(data.message);
        } else if (data.detail) {
          setError(data.detail);
        } else {
          setError("");
        }
        return;
      }
      // Mensaje de éxito del backend: omitido, usamos modal
      // Si el backend devuelve access, refresh y user, guarda igual que en login
      if (data.access && data.user && data.refresh) {
        const userObj = {
          username: data.user.username,
          email: data.user.email,
          access: data.access,
          refresh: data.refresh,
        };
        setUserCookie(userObj);
        // Si usas contexto, también puedes hacer: setUser(userObj);
        navigate("/");
      } else {
        // Si no devuelve tokens, muestra modal de verificación
        setShowVerifyModal(true);
      }
    } catch (err) {
      setError(t('register.errors.generic'));
    }
  };

  return (
    <div className="register-container">
      <ConfirmModal
        isOpen={showVerifyModal}
        title={t('register.verify.title')}
        message={t('register.verify.message')}
        confirmText={t('register.verify.go_login')}
        onConfirm={() => {
          setShowVerifyModal(false);
          navigate("/login");
        }}
        onCancel={() => setShowVerifyModal(false)}
        showCancel={false}
        confirmVariant="primary"
      />
      <h2 className="register-title">{t('register.title')}</h2>

      <p className="register-text">
        {t('register.cta_login_prefix')} {" "}
        <button
          type="button"
          className="as-link"
          onClick={() => {
            if (onBack) onBack();
          }}
        >
          {t('register.cta_login_link')}
        </button>
      </p>
      <form onSubmit={handleRegister} className="register-form">
        <label htmlFor="username">{t('register.fields.username_label')}</label>
        <input
          type="text"
          placeholder={t('register.fields.username_placeholder')}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="register-input"
          required
        />
        <label htmlFor="name">{t('register.fields.name_label')}</label>
        <input
          type="text"
          placeholder={t('register.fields.name_placeholder')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="register-input"
        />
        <label htmlFor="email">{t('register.fields.email_label')}</label>
        <input
          type="email"
          placeholder={t('register.fields.email_placeholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="register-input"
          required
        />
        <label htmlFor="password">{t('register.fields.password_label')}</label>
        <div className="password-input-wrapper-register">
          <input
            type={showPassword ? "text" : "password"}
            placeholder={t('register.fields.password_placeholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="register-input-password"
            required
          />
          <button
            type="button"
            className="password-toggle-btn-auth"
            aria-label={t('register.fields.password_label')}
            onClick={() => setShowPassword((v) => !v)}
            tabIndex={0}
          >
            {showPassword ? (
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#888"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M1 1l22 22" />
                <path d="M17.94 17.94A10.05 10.05 0 0 1 12 19C7 19 2.73 15.11 1 12c.74-1.32 1.81-2.87 3.22-4.29M9.53 9.53A3.5 3.5 0 0 1 12 8.5a3.5 3.5 0 0 1 3.5 3.5c0 .47-.09.91-.26 1.32" />
              </svg>
            ) : (
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#888"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <ellipse cx="12" cy="12" rx="10" ry="7" />
                <circle cx="12" cy="12" r="3.5" />
              </svg>
            )}
          </button>
        </div>
        <label htmlFor="password_confirm">{t('register.fields.password_confirm_label')}</label>
        <div className="password-input-wrapper-register">
          <input
            type={showPasswordConfirm ? "text" : "password"}
            placeholder={t('register.fields.password_confirm_placeholder')}
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            className="register-input-password"
            required
          />
          <button
            type="button"
            className="password-toggle-btn-auth"
            aria-label={t('register.fields.password_confirm_label')}
            onClick={() => setShowPasswordConfirm((v) => !v)}
            tabIndex={0}
          >
            {showPasswordConfirm ? (
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#888"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M1 1l22 22" />
                <path d="M17.94 17.94A10.05 10.05 0 0 1 12 19C7 19 2.73 15.11 1 12c.74-1.32 1.81-2.87 3.22-4.29M9.53 9.53A3.5 3.5 0 0 1 12 8.5a3.5 3.5 0 0 1 3.5 3.5c0 .47-.09.91-.26 1.32" />
              </svg>
            ) : (
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#888"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <ellipse cx="12" cy="12" rx="10" ry="7" />
                <circle cx="12" cy="12" r="3.5" />
              </svg>
            )}
          </button>
        </div>
        <label className="login-consent">
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
          <span className="login-consent-text">
            {t('login.consent.label')} <a href="https://getowly.ai/terms-of-service" className="as-link">{t('login.consent.terms')}</a> {t('login.consent.and')} <a href="https://getowly.ai/privacy-policy" className="as-link">{t('login.consent.privacy')}</a>
          </span>
        </label>
        {Array.isArray(error) ? (
          <ul className="register-error-list">
            {error.map((msg, idx) => (
              <li key={idx} className="register-error">
                {msg}
              </li>
            ))}
          </ul>
        ) : (
          error && <div className="register-error">{error}</div>
        )}
        {/* Se oculta el banner de éxito; se usa modal en su lugar */}
        <div className="register-buttons">
          <Button type="submit" variant="primary" size="md" className="register-button">
            {t('register.submit')}
          </Button>
        </div>
      </form>
    </div>
  );
}
